import { useCallback, useEffect, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import type { Issue } from "../../../models/project";
import { friendlyMessage, listIssues } from "../../../controllers/projectApi";
import type { CreateTraceLinkRequest, Document, TraceEndpointType, TraceLink, TraceLinkType } from "../../../models/eqms";
import { WORK_AREA_LABEL } from "../../../models/eqms";
import { createTraceLink, deleteTraceLink, listDocuments, listTraceLinks } from "../../../controllers/eqmsApi";

const LINK_TYPE_LABEL: Record<TraceLinkType, string> = {
  DERIVES: "파생",
  IMPLEMENTS: "구현",
  VERIFIES: "검증",
  SATISFIES: "충족",
  REFERENCES: "참조",
  SUPERSEDES: "대체",
};
const LINK_TYPES: TraceLinkType[] = ["DERIVES", "IMPLEMENTS", "VERIFIES", "SATISFIES", "REFERENCES", "SUPERSEDES"];

interface MatrixColumn {
  key: string;
  label: string;
  type: TraceEndpointType;
  id: string;
}

// 추적성: 프로젝트(선택 시 워크 영역) 단위 요구사항 ↔ 설계/테스트/문서 추적성 매트릭스.
export function EqmsTraceView({ eqms }: { eqms: EqmsController }) {
  const projectId = eqms.projectId;
  const [issues, setIssues] = useState<Issue[]>([]);
  const [traceLinks, setTraceLinks] = useState<TraceLink[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [allIssues, links, docs] = await Promise.all([
        listIssues(eqms.getToken, projectId, eqms.workArea ?? undefined),
        listTraceLinks(eqms.getToken, projectId),
        listDocuments(eqms.getToken, projectId),
      ]);
      setIssues(allIssues);
      setTraceLinks(links);
      setDocuments(docs);
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [eqms.getToken, projectId, eqms.workArea]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function addLink(req: CreateTraceLinkRequest) {
    if (!projectId) return;
    setError(null);
    try {
      await createTraceLink(eqms.getToken, projectId, req);
      await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function removeLink(id: string) {
    setError(null);
    try {
      await deleteTraceLink(eqms.getToken, id);
      setTraceLinks((cur) => cur.filter((l) => l.id !== id));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  if (!projectId) {
    return <div className="note">프로젝트를 먼저 선택하세요.</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>
          추적성 매트릭스{eqms.workArea ? ` · ${WORK_AREA_LABEL[eqms.workArea]}` : ""}
        </h3>
      </div>

      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}

      {loading ? (
        <div className="center">불러오는 중…</div>
      ) : (
        <TraceMatrix issues={issues} traceLinks={traceLinks} documents={documents} onAddLink={addLink} onDeleteLink={removeLink} />
      )}
    </div>
  );
}

function TraceMatrix({
  issues,
  traceLinks,
  documents,
  onAddLink,
  onDeleteLink,
}: {
  issues: Issue[];
  traceLinks: TraceLink[];
  documents: Document[];
  onAddLink: (req: CreateTraceLinkRequest) => Promise<void>;
  onDeleteLink: (id: string) => Promise<void>;
}) {
  const [addingFor, setAddingFor] = useState<string | null>(null);

  const requirements = issues.filter((i) => i.type === "REQUIREMENT");
  const designs = issues.filter((i) => i.type === "DESIGN");
  const testItems = issues.filter((i) => i.type === "TEST_ITEM");

  const columns: MatrixColumn[] = [
    ...designs.map((d): MatrixColumn => ({ key: `ISSUE:${d.id}`, label: d.key, type: "ISSUE", id: d.id })),
    ...testItems.map((t): MatrixColumn => ({ key: `ISSUE:${t.id}`, label: t.key, type: "ISSUE", id: t.id })),
    ...documents.map((d): MatrixColumn => ({ key: `DOCUMENT:${d.id}`, label: d.docNumber, type: "DOCUMENT", id: d.id })),
  ];

  function linkBetween(reqId: string, col: MatrixColumn): TraceLink | undefined {
    return traceLinks.find(
      (l) =>
        (l.sourceType === "ISSUE" && l.sourceId === reqId && l.targetType === col.type && l.targetId === col.id) ||
        (l.targetType === "ISSUE" && l.targetId === reqId && l.sourceType === col.type && l.sourceId === col.id),
    );
  }

  if (columns.length === 0) {
    return <div className="sdui-list-empty">설계/테스트 항목 또는 문서가 없어 매트릭스를 표시할 수 없습니다.</div>;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="sdui-table">
        <thead>
          <tr>
            <th>요구사항</th>
            {columns.map((c) => (
              <th key={c.key} className="mono">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {requirements.map((req) => {
            const hasAnyLink = columns.some((c) => linkBetween(req.id, c));
            return (
              <tr key={req.id} style={!hasAnyLink ? { background: "#fdeef0" } : undefined}>
                <td>
                  <span className="mono">{req.key}</span> {req.summary}
                  {!hasAnyLink && (
                    <span className="tag" style={{ marginLeft: 6, color: "var(--amber)" }}>
                      미추적
                    </span>
                  )}
                </td>
                {columns.map((c) => {
                  const link = linkBetween(req.id, c);
                  const cellKey = `${req.id}|${c.key}`;
                  return (
                    <td key={c.key} style={{ textAlign: "center" }}>
                      {link ? (
                        <span
                          className="tag"
                          title={link.note || undefined}
                          style={{ cursor: "pointer" }}
                          onClick={() => void onDeleteLink(link.id)}
                        >
                          {LINK_TYPE_LABEL[link.linkType]} ✕
                        </span>
                      ) : (
                        <button
                          className="btn"
                          style={{ width: "auto", padding: "2px 8px" }}
                          onClick={() => setAddingFor(addingFor === cellKey ? null : cellKey)}
                        >
                          + 링크 추가
                        </button>
                      )}
                      {addingFor === cellKey && (
                        <AddLinkInline
                          onSubmit={async (linkType, note) => {
                            await onAddLink({
                              sourceType: "ISSUE",
                              sourceId: req.id,
                              targetType: c.type,
                              targetId: c.id,
                              linkType,
                              note: note || undefined,
                            });
                            setAddingFor(null);
                          }}
                          onCancel={() => setAddingFor(null)}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {requirements.length === 0 && <div className="sdui-list-empty">요구사항이 없습니다.</div>}
    </div>
  );
}

function AddLinkInline({
  onSubmit,
  onCancel,
}: {
  onSubmit: (linkType: TraceLinkType, note: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [linkType, setLinkType] = useState<TraceLinkType>("VERIFIES");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await onSubmit(linkType, note);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
      <select className="sdui-input" value={linkType} onChange={(e) => setLinkType(e.target.value as TraceLinkType)}>
        {LINK_TYPES.map((t) => (
          <option key={t} value={t}>
            {LINK_TYPE_LABEL[t]}
          </option>
        ))}
      </select>
      <input className="sdui-input" placeholder="메모" value={note} onChange={(e) => setNote(e.target.value)} />
      <div style={{ display: "flex", gap: 4 }}>
        <button className="btn primary" style={{ width: "auto" }} disabled={busy} onClick={submit}>
          추가
        </button>
        <button className="btn" style={{ width: "auto" }} onClick={onCancel}>
          취소
        </button>
      </div>
    </div>
  );
}
